<template>
  <UPage class="flex-1 flex flex-col min-h-0" :ui="{ center: 'flex flex-col flex-1 min-h-0' }">
    <UPageHeader title="Adressen" :description="tenantName ? `Mandant: ${tenantName}` : undefined">
      <template #links>
        <UButton
          to="/microtech-hub/addresses"
          color="neutral"
          icon="i-heroicons-arrow-left"
          label="Zurück"
          variant="ghost"
        />
        <UButton
          color="neutral"
          icon="i-heroicons-cog-6-tooth"
          label="Spalten"
          variant="outline"
          @click="openColumnSelector"
        />
        <UButton
          :loading="loading"
          color="primary"
          icon="i-heroicons-arrow-path"
          label="Aktualisieren"
          @click="() => fetchAddresses(false)"
        />
      </template>
    </UPageHeader>

    <UPageBody class="p-0 sm:p-0 flex flex-col min-h-0 h-full">
      <PortalTable
        ref="portalTable"
        :all-columns="allPossibleColumns"
        :columns-loading="loading && allPossibleColumns.length === 0"
        :data="filteredAddresses"
        :loading="loading"
        :selected-columns="selectedColumns"
        :sort-storage-key="SORT_STORAGE_KEY"
        :total="hasActiveVatStatusFilter ? filteredAddresses.length : totalCount"
        empty-description="Es konnten keine Adressen für diesen Mandanten geladen werden."
        empty-icon="i-heroicons-magnifying-glass"
        empty-title="Keine Adressen gefunden"
        class="h-full"
        @load-more="fetchAddresses(true)"
        @reset:columns="resetToDefault"
        @select="onRowClick"
        @update:columns="updateColumns"
      >
        <template v-if="isAddressVatCheckActive" #header-actions>
          <div class="flex items-center gap-2">
            <USelectMenu
              v-model="selectedVatStatusFilter"
              :items="vatStatusOptions"
              value-key="value"
              multiple
              placeholder="USt-IdNr.-Status filtern..."
              class="w-56"
              :ui="{ content: 'w-64' }"
            >
              <template #leading>
                <UIcon
                  name="i-lucide-filter"
                  class="w-4 h-4 text-gray-400 dark:text-gray-500"
                  :class="{ 'text-primary': hasActiveVatStatusFilter }"
                />
              </template>
            </USelectMenu>
            <span
              v-if="hasActiveVatStatusFilter"
              class="text-xs text-neutral-500 font-medium whitespace-nowrap"
            >
              {{ filteredAddresses.length }} von {{ addresses.length }}
            </span>
            <UButton
              v-if="hasActiveVatStatusFilter"
              icon="i-lucide-filter-x"
              variant="ghost"
              color="neutral"
              size="xs"
              label="Zurücksetzen"
              @click="resetVatStatusFilter"
            />
          </div>
        </template>

        <template #vatStatus-header="{ column, toggleSort }">
          <div class="flex items-center gap-1.5">
            <div
              class="flex items-center gap-2 cursor-pointer select-none"
              aria-label="Sort by vatStatus"
              @click="toggleSort ? toggleSort() : onVatStatusSort(column)"
            >
              <span class="truncate">USt-IdNr.-Prüfung</span>
              <UIcon
                v-if="column.getIsSorted()"
                :name="column.getIsSorted() === 'asc' ? 'i-heroicons-bars-arrow-up' : 'i-heroicons-bars-arrow-down'"
                class="w-4 h-4 text-primary shrink-0"
              />
            </div>

            <UPopover
              v-model:open="isVatFilterPopoverOpen"
              :ui="{ content: 'w-72 p-3' }"
            >
              <UButton
                icon="i-heroicons-funnel"
                size="xs"
                variant="ghost"
                color="neutral"
                class="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                :class="{ 'text-primary dark:text-primary-400 font-bold bg-primary-50 dark:bg-primary-950/40': hasActiveVatStatusFilter }"
                :title="hasActiveVatStatusFilter ? `Filter aktiv (${activeStatusValues.length})` : 'USt-IdNr.-Status filtern'"
                aria-label="USt-IdNr.-Status filtern"
              />
              <template #content>
                <div class="space-y-3">
                  <div class="flex items-center justify-between pb-2 border-b border-gray-100 dark:border-gray-800">
                    <span class="text-xs font-semibold text-gray-700 dark:text-gray-200">
                      USt-IdNr.-Status filtern
                    </span>
                    <UBadge v-if="hasActiveVatStatusFilter" color="primary" variant="subtle" size="xs">
                      {{ activeStatusValues.length }} aktiv
                    </UBadge>
                  </div>

                  <div class="space-y-1 max-h-60 overflow-y-auto">
                    <label
                      v-for="opt in vatStatusOptions"
                      :key="opt.value"
                      class="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer select-none text-xs"
                      @click.prevent="toggleStatusFilter(opt.value)"
                    >
                      <div class="flex items-center gap-2">
                        <UCheckbox
                          :model-value="isStatusSelected(opt.value)"
                          @click.stop
                          @update:model-value="toggleStatusFilter(opt.value)"
                        />
                        <span class="font-medium text-gray-800 dark:text-gray-200">{{ opt.value }}</span>
                      </div>
                      <UBadge :color="opt.color || 'neutral'" variant="subtle" size="xs">
                        {{ opt.count }}
                      </UBadge>
                    </label>
                  </div>

                  <div class="pt-2 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2">
                    <UButton
                      v-if="hasActiveVatStatusFilter"
                      size="xs"
                      variant="ghost"
                      color="neutral"
                      label="Alle abwählen"
                      @click="resetVatStatusFilter"
                    />
                    <UButton
                      v-else
                      size="xs"
                      variant="ghost"
                      color="neutral"
                      label="Alle auswählen"
                      @click="selectAllVatStatuses"
                    />
                    <UButton
                      size="xs"
                      variant="ghost"
                      color="primary"
                      label="Schließen"
                      @click="isVatFilterPopoverOpen = false"
                    />
                  </div>
                </div>
              </template>
            </UPopover>
          </div>
        </template>

        <template #vatStatus-cell="{ row }">
          <div
            class="flex items-center gap-1.5"
            @click.stop="onVatStatusClick(row.original)"
          >
            <UBadge
              :color="getVatBadge(row.original).color"
              variant="subtle"
              size="xs"
              class="font-medium inline-flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
              :title="getVatBadge(row.original).tooltip"
            >
              <UIcon :name="getVatBadge(row.original).icon" class="w-3.5 h-3.5" />
              <span>{{ getVatBadge(row.original).label }}</span>
            </UBadge>
          </div>
        </template>
      </PortalTable>
    </UPageBody>
  </UPage>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import PortalTable from '@/components/PortalTable.vue'
import { useAddressStore } from '@/stores/address'
import * as introspection from '@/utils/graphql-introspection'
import { useAuthStore } from '@/stores/auth'
import { vatValidationApi, type AddressStatusSummary } from '@/api/vatValidation'
import { getVatStatusBadgeInfo } from '@/utils/vatCodes'

const route = useRoute()
const authStore = useAuthStore()
const addressStore = useAddressStore()
const router = useRouter()

const customerNo = computed(() => authStore.userInfo?.activeTenant?.customerNo || (route.query.customer as string) || '')
const tenantName = computed(() => authStore.userInfo?.activeTenant?.tenantName || (route.query.tenant as string) || '')

const loading = ref(false)
const addresses = ref<Record<string, unknown>[]>([])
const allPossibleColumns = ref<Array<{ key: string; label: string; graphql?: string }>>([])
const selectedColumns = ref<
  Array<{ key: string; label: string; locked?: boolean; graphql?: string }>
>([])
const portalTable = ref<InstanceType<typeof PortalTable> | null>(null)

const endCursor = ref('')
const hasNextPage = ref(false)
const limit = ref(1000)
const totalCount = ref(0)

const microtechConfig = ref<{
  active?: boolean
  vatValidation?: {
    active?: boolean
    checkOnAddressCreate?: boolean
    checkOnAddressUpdate?: boolean
    periodicCheckEnabled?: boolean
    [key: string]: unknown
  }
} | null>(null)

const isAddressVatCheckActive = computed(() => {
  const vat = microtechConfig.value?.vatValidation
  if (!vat || !vat.active) return false
  const addrCreate = vat.checkOnAddressCreate ?? true
  const addrUpdate = vat.checkOnAddressUpdate ?? true
  const periodic = vat.periodicCheckEnabled ?? false
  return Boolean(addrCreate || addrUpdate || periodic)
})

const addressVatStatuses = ref<Record<string, AddressStatusSummary>>({})
const vatStatusesLoading = ref(false)

const STORAGE_KEY = computed(() => `cc_addr_cols_${customerNo.value}_${tenantName.value}`)
const SORT_STORAGE_KEY = computed(() => `cc_addr_sort_${customerNo.value}_${tenantName.value}`)

function normalizeAdrNr(val: unknown): string {
  if (val == null) return ''
  return String(val).trim()
}

function getAddressSummary(adrVal: unknown): AddressStatusSummary | undefined {
  const raw = normalizeAdrNr(adrVal)
  if (!raw) return undefined
  if (addressVatStatuses.value[raw]) return addressVatStatuses.value[raw]
  const parsed = parseInt(raw, 10)
  if (!isNaN(parsed) && addressVatStatuses.value[String(parsed)]) {
    return addressVatStatuses.value[String(parsed)]
  }
  return undefined
}

async function fetchMicrotechConfig() {
  try {
    const res = await axios.get('/api/microtech/v1/config')
    microtechConfig.value = res?.data || null
  } catch (e) {
    console.error('Fehler beim Laden der microtech-Konfiguration:', e)
    microtechConfig.value = null
  }
}

async function fetchAddressVatStatuses(addressNrs: string[]) {
  if (!isAddressVatCheckActive.value) return
  vatStatusesLoading.value = true
  try {
    const response = await vatValidationApi.getAddressStatuses(
      addressNrs.length > 0 ? addressNrs : undefined,
      tenantName.value || undefined,
      customerNo.value || undefined,
    )
    if (response.data) {
      addressVatStatuses.value = {
        ...addressVatStatuses.value,
        ...response.data,
      }
      enrichAddressesWithVatStatus()
    }
  } catch (err) {
    console.error('Fehler beim Laden der USt-IdNr.-Prüfstatus:', err)
  } finally {
    vatStatusesLoading.value = false
  }
}

function enrichAddressesWithVatStatus() {
  if (!isAddressVatCheckActive.value) return
  addresses.value = addresses.value.map((addr) => {
    const summary = getAddressSummary(getValue(addr, 'fldAdrNr'))
    const badge = getVatStatusBadgeInfo(summary)
    return {
      ...addr,
      vatStatus: badge.label,
    }
  })
}

function getVatBadge(rowData: Record<string, unknown>) {
  const summary = getAddressSummary(getValue(rowData, 'fldAdrNr'))
  return getVatStatusBadgeInfo(summary)
}

function onVatStatusClick(rowData: Record<string, unknown>) {
  const adrNr = normalizeAdrNr(getValue(rowData, 'fldAdrNr'))
  if (adrNr) {
    router.push(`/microtech-hub/addresses/${adrNr}?tab=vatvalidation`)
  }
}

const selectedVatStatusFilter = ref<string[]>([])
const isVatFilterPopoverOpen = ref(false)

const activeStatusValues = computed<string[]>(() => {
  if (!selectedVatStatusFilter.value || selectedVatStatusFilter.value.length === 0) return []
  return (selectedVatStatusFilter.value as unknown as Array<string | { value?: unknown }>).map((item) =>
    typeof item === 'object' && item !== null && 'value' in item ? String(item.value) : String(item),
  )
})

const hasActiveVatStatusFilter = computed(() => activeStatusValues.value.length > 0)

function isStatusSelected(status: string): boolean {
  return activeStatusValues.value.includes(status)
}

function toggleStatusFilter(status: string) {
  if (isStatusSelected(status)) {
    selectedVatStatusFilter.value = (
      selectedVatStatusFilter.value as unknown as Array<string | { value?: unknown }>
    )
      .filter((item) => {
        const val = typeof item === 'object' && item !== null && 'value' in item ? String(item.value) : String(item)
        return val !== status
      })
      .map((item) => (typeof item === 'object' && item !== null && 'value' in item ? String(item.value) : String(item)))
  } else {
    selectedVatStatusFilter.value = [...selectedVatStatusFilter.value, status]
  }
}

function selectAllVatStatuses() {
  selectedVatStatusFilter.value = vatStatusOptions.value.map((opt) => opt.value)
}

function onVatStatusSort(column?: { toggleSorting?: (desc?: boolean) => void }) {
  if (portalTable.value?.toggleColumnSort) {
    portalTable.value.toggleColumnSort('vatStatus', column)
  } else if (column?.toggleSorting) {
    column.toggleSorting()
  }
}

const standardStatuses = [
  { value: 'Inland: Gültig', label: 'Inland: Gültig', color: 'success' },
  { value: 'Inland: Ungültig', label: 'Inland: Ungültig', color: 'error' },
  { value: 'Ausland: Gültig', label: 'Ausland: Gültig', color: 'success' },
  { value: 'Ausland: Eingeschränkt gültig', label: 'Ausland: Eingeschränkt gültig', color: 'warning' },
  { value: 'Ausland: Ungültig', label: 'Ausland: Ungültig', color: 'error' },
  { value: 'Keine USt-IdNr.', label: 'Keine USt-IdNr.', color: 'error' },
  { value: 'Nicht geprüft', label: 'Nicht geprüft', color: 'neutral' },
  { value: 'Prüffehler', label: 'Prüffehler', color: 'error' },
]

const vatStatusOptions = computed(() => {
  const counts = new Map<string, number>()
  addresses.value.forEach((addr) => {
    const status = (getValue(addr, 'vatStatus') as string) || 'Nicht geprüft'
    counts.set(status, (counts.get(status) || 0) + 1)
  })

  const options: { value: string; label: string; count: number; color?: string }[] = []
  const seen = new Set<string>()

  for (const item of standardStatuses) {
    seen.add(item.value)
    const count = counts.get(item.value) || 0
    options.push({
      value: item.value,
      label: count > 0 ? `${item.label} (${count})` : item.label,
      count,
      color: item.color,
    })
  }

  for (const [status, count] of counts.entries()) {
    if (!seen.has(status)) {
      options.push({
        value: status,
        label: `${status} (${count})`,
        count,
      })
    }
  }

  return options
})

const filteredAddresses = computed(() => {
  if (activeStatusValues.value.length === 0) {
    return addresses.value
  }
  return addresses.value.filter((addr) => {
    const status = (getValue(addr, 'vatStatus') as string) || 'Nicht geprüft'
    return activeStatusValues.value.includes(status)
  })
})

function resetVatStatusFilter() {
  selectedVatStatusFilter.value = []
}

async function fetchMetadata() {
  if (!customerNo.value || !tenantName.value) return
  try {
    await fetchMicrotechConfig()

    const fields = await introspection.fetchTableFields(
      'tblAddresses',
      customerNo.value,
      tenantName.value,
    )
    console.log('fetchMetadata fields received:', fields.length)

    const columns: Array<{ key: string; label: string; graphql?: string }> = []

    // Wir sammeln erst alle Basis-Felder
    for (const f of fields) {
      if (
        f.name.startsWith('_') ||
        f.name.endsWith('_int') ||
        f.name.startsWith('tbl') ||
        f.name.startsWith('lnk')
      )
        continue

      if (f.name.startsWith('row')) {
        // 1:1 Relation auflösen (z.B. rowReAnsNr)
        // Wir holen nur die Felder für rowReAnsNr und rowLiAnsNr als Beispiele
        if (f.name === 'rowReAnsNr' || f.name === 'rowLiAnsNr') {
          const relFields = await introspection.fetchRelationFields(
            f.type,
            customerNo.value,
            tenantName.value,
          )
          const prefix = f.name === 'rowReAnsNr' ? 'Rechnung' : 'Lieferung'
          relFields.forEach((rf) => {
            if (rf.isRelation || rf.name.startsWith('_')) return
            columns.push({
              key: `${f.name}.${rf.name}`,
              label: rf.label || `${prefix}: ${rf.name}`,
              graphql: rf.graphql ? `${f.name} { ${rf.graphql} }` : `${f.name} { ${rf.name} }`,
            })
          })
        }
        continue
      }

      columns.push({
        key: f.name,
        label: f.label || f.description || f.name,
        graphql: f.graphql,
      })
    }

    if (isAddressVatCheckActive.value) {
      const adrIdx = columns.findIndex((c) => c.key === 'fldAdrNr')
      if (adrIdx !== -1) {
        columns.splice(adrIdx + 1, 0, { key: 'vatStatus', label: 'USt-IdNr.-Prüfung' })
      } else {
        columns.unshift({ key: 'vatStatus', label: 'USt-IdNr.-Prüfung' })
      }
    }

    allPossibleColumns.value = columns

    // Update selected columns labels from metadata
    if (selectedColumns.value.length > 0) {
      selectedColumns.value = selectedColumns.value.map((sc) => {
        const found = columns.find((c) => c.key === sc.key)
        if (found) {
          return { ...sc, label: found.label, graphql: found.graphql }
        }
        return sc
      })
    }

    loadSavedColumns()
  } catch (error) {
    console.error('Fehler beim Laden der Metadaten:', error)
  }
}

function loadSavedColumns() {
  const saved = addressStore.loadColumns(STORAGE_KEY.value)
  if (saved) {
    // Migration/Validation: Ensure fldAdrNr is present and locked
    let updated = [...saved]

    if (isAddressVatCheckActive.value) {
      if (!updated.some((c) => c.key === 'vatStatus')) {
        const adrIdx = updated.findIndex((c) => c.key === 'fldAdrNr')
        if (adrIdx !== -1) {
          updated.splice(adrIdx + 1, 0, { key: 'vatStatus', label: 'USt-IdNr.-Prüfung' })
        } else {
          updated.push({ key: 'vatStatus', label: 'USt-IdNr.-Prüfung' })
        }
      }
    } else {
      updated = updated.filter((c) => c.key !== 'vatStatus')
    }

    // Update labels from allPossibleColumns if they match
    updated = updated.map((p) => {
      const found = allPossibleColumns.value.find((c) => c.key === p.key)
      if (found) {
        return { ...p, label: found.label, graphql: found.graphql }
      }
      return p
    })

    if (!updated.find((c: { key: string }) => c.key === 'fldAdrNr')) {
      updated.unshift({ key: 'fldAdrNr', label: 'Adressnummer', locked: true })
    } else {
      const idx = updated.findIndex((c: { key: string }) => c.key === 'fldAdrNr')
      updated[idx].locked = true
    }
    selectedColumns.value = updated
  } else {
    setDefaultColumns()
  }
}

function setDefaultColumns() {
  const defaults: Array<{ key: string; label: string; locked?: boolean; graphql?: string }> = [
    { key: 'fldAdrNr', label: 'Adressnummer', locked: true },
  ]
  if (isAddressVatCheckActive.value) {
    defaults.push({ key: 'vatStatus', label: 'USt-IdNr.-Prüfung' })
  }
  defaults.push(
    { key: 'fldStatus', label: 'Status' },
    { key: 'rowReAnsNr.fldNa2', label: 'Rechnungsanschrift: Name 2' },
    { key: 'rowReAnsNr.fldOrt', label: 'Rechnungsanschrift: Ort' },
    { key: 'rowReAnsNr.fldEMail1', label: 'E-Mail' },
  )

  // Use metadata labels if available
  const updatedDefaults = defaults.map((d) => {
    const found = allPossibleColumns.value.find((c) => c.key === d.key)
    if (found) {
      return { ...d, label: found.label, graphql: found.graphql }
    }
    return d
  })

  selectedColumns.value = updatedDefaults
  addressStore.setColumns(STORAGE_KEY.value, updatedDefaults, false)
}

function resetToDefault() {
  addressStore.removeColumns(STORAGE_KEY.value)
  setDefaultColumns()
  addresses.value = []
  endCursor.value = ''
  fetchAddresses()
}

function updateColumns(newCols: Array<{ key: string; label: string; locked?: boolean }>) {
  selectedColumns.value = newCols
  addressStore.setColumns(STORAGE_KEY.value, newCols)
  addresses.value = []
  endCursor.value = ''
  fetchAddresses()
}

function openColumnSelector() {
  portalTable.value?.openColumnSelector()
}

function onRowClick(row: { original: Record<string, unknown> }) {
  const rowData = row.original
  if (!rowData) return
  const adrNr = getValue(rowData, 'fldAdrNr')
  if (adrNr) {
    router.push(`/microtech-hub/addresses/${adrNr}`)
  }
}

async function fetchAddresses(loadMore = false) {
  if (selectedColumns.value.length === 0 || loading.value) return
  if (loadMore && (!hasNextPage.value || !endCursor.value)) return

  loading.value = true
  try {
    const selectionSet = buildSelectionSet(selectedColumns.value)

    let currentAfter = loadMore && endCursor.value ? endCursor.value : ''
    let moreAvailable = true
    const allFetchedNodes: Record<string, unknown>[] = loadMore ? [...addresses.value] : []

    while (moreAvailable) {
      const afterParam = currentAfter ? `after: "${currentAfter}"` : ''
      const query = `
        query {
          tblAddresses {
            conRead(first: ${limit.value}, ${afterParam}) {
              pageInfo {
                endCursor
                hasNextPage
                edgeCount
              }
              edges {
                node {
                  ${selectionSet}
                }
              }
            }
          }
        }
      `
      const response = await axios.post('/api/microtech/v1/proxy/graphql', { query })
      const conRead = response.data?.data?.tblAddresses?.conRead

      if (!conRead) {
        console.error('Keine tblAddresses.conRead Daten in der Antwort:', response.data)
        moreAvailable = false
        break
      }

      const newBatch = conRead.edges.map((edge: { node: Record<string, unknown> }) => edge.node)
      allFetchedNodes.push(...newBatch)

      currentAfter = conRead.pageInfo.endCursor
      moreAvailable = conRead.pageInfo.hasNextPage

      // Update intermediate state if needed, but for virtualization we might want to wait until all are loaded
      // or update incrementally. User said "complete dataset needs to be fetched, with a loop".
      addresses.value = [...allFetchedNodes]
      endCursor.value = currentAfter
      hasNextPage.value = moreAvailable
      totalCount.value = conRead.pageInfo.edgeCount || addresses.value.length

      // Safety break to avoid infinite loop if something goes wrong with pageInfo
      if (newBatch.length === 0) moreAvailable = false
    }

    if (isAddressVatCheckActive.value && addresses.value.length > 0) {
      enrichAddressesWithVatStatus()
      const addressNrs = Array.from(
        new Set(
          addresses.value
            .map((a) => normalizeAdrNr(getValue(a, 'fldAdrNr')))
            .filter((nr) => nr.length > 0),
        ),
      )
      if (addressNrs.length > 0) {
        fetchAddressVatStatuses(addressNrs)
      }
    }
  } catch (error) {
    console.error('Fehler beim Laden der Adressen:', error)
  } finally {
    loading.value = false
  }
}

function buildSelectionSet(columns: Array<{ key: string; graphql?: string }>) {
  const customSelections = new Set<string>()
  const tree: Record<string, unknown> = {
    fldAdrNr: {},
  }

  columns.forEach((col) => {
    if (col.key === 'vatStatus') return

    if (col.graphql && col.graphql.includes('{')) {
      customSelections.add(col.graphql)
    } else {
      const parts = (col.graphql || col.key).split('.')
      let current = tree as Record<string, unknown>
      parts.forEach((part) => {
        if (!current[part]) {
          current[part] = {} as Record<string, unknown>
        }
        current = current[part] as Record<string, unknown>
      })
    }
  })

  function stringify(obj: Record<string, unknown>): string {
    return Object.keys(obj)
      .map((k) => {
        const val = obj[k]
        if (val && typeof val === 'object' && Object.keys(val).length > 0) {
          const sub = stringify(val as Record<string, unknown>)
          return `${k} { ${sub} }`
        }
        return k
      })
      .join('\n')
  }

  const baseSelection = stringify(tree)
  return [baseSelection, ...Array.from(customSelections)].filter((s) => s).join('\n')
}

function getValue(obj: Record<string, unknown>, key: string): unknown {
  return key.split('.').reduce<unknown>((o, i) => {
    if (o && typeof o === 'object' && i in (o as Record<string, unknown>)) {
      return (o as Record<string, unknown>)[i]
    }
    return undefined
  }, obj)
}

onMounted(async () => {
  if (customerNo.value && tenantName.value) {
    await fetchMetadata()
    fetchAddresses()
  }

  watch([customerNo, tenantName], async ([newCust, newTen]) => {
    if (newCust && newTen) {
      addressVatStatuses.value = {}
      await fetchMetadata()
      fetchAddresses()
    }
  })
})
</script>
